import QueryString from "qs"

import { ErrorHandling } from "@/lib/error_handling"
import type { Geolocation } from "@/lib/geolocation"

import {
  extractLatlngFromLocationIQResponse,
  transformLocationIQAddressToBingFormat,
} from "./transform_response"
import type { LocationIQ } from "./types"

export const fetchGeolocationAutocompleteFromLocationIQ = async ({
  query,
  countryFilter = `nz`,
  limit = 10,
}: {
  query: string
  countryFilter?: string
  limit?: number
}): Promise<{
  suggestions: Geolocation.Bing.Address[]
  coordinates: Geolocation.Latlng[]
}> => {
  const params = QueryString.stringify({
    key: process.env.NEXT_PUBLIC_LOCATIONIQ_API_KEY,
    q: query,
    countrycodes: countryFilter,
    limit,
    format: `json`,
    dedupe: 1,
  })

  const completion = await ErrorHandling.withRetries({
    process: async () => {
      const response = await fetch(
        `https://api.locationiq.com/v1/autocomplete?${params}`
      )

      if (!response.ok) {
        throw new Error(`LocationIQ API error: ${response.status}`)
      }

      return response.json() as Promise<LocationIQ.AutocompleteResponse[]>
    },
    getFallbackResult: () => {
      return []
    },
    onFatalError: () => {},
    processName: `fetchGeolocationAutocompleteFromLocationIQ`,
  })

  if (!completion.result) {
    console.error(
      `fetchGeolocationAutocompleteFromLocationIQ failed but did not use provided fallback result.`,
      completion
    )
    return { suggestions: [], coordinates: [] }
  }

  // Transform LocationIQ responses to our internal format
  const suggestions = completion.result.map(
    transformLocationIQAddressToBingFormat
  )
  const coordinates = completion.result.map(extractLatlngFromLocationIQResponse)

  return { suggestions, coordinates }
}
